/**
 * 【チャットサジェスチョンコンポーネント】
 * 初回チャットセッション時にユーザーに表示するサジェスチョンボタン
 */

import type React from "react";
import type { AIChatDocumentType } from "../types/documentType";
import styles from "./ChatSuggestion.module.scss";

interface ChatSuggestionProps {
  /** サジェスチョンがクリックされたときのハンドラー */
  onSuggestionClick: (suggestion: string) => void;
  /** ドキュメントタイプ */
  documentType: AIChatDocumentType;
}

/**
 * ドキュメントタイプごとのデフォルトサジェスチョン
 */
const DEFAULT_SUGGESTIONS: Record<AIChatDocumentType, string[]> = {
  "system-proposal": [
    "システムの概要を教えてください",
    "このシステムの目的は何ですか？",
    "主要な機能について説明してください",
  ],
  "functional-requirements": [
    "機能要件の概要を教えてください",
    "ユーザーストーリーを確認したい",
    "画面遷移について説明してください",
  ],
  "non-functional-requirements": [
    "非機能要件の概要を教えてください",
    "パフォーマンス要件について教えてください",
    "セキュリティ要件について教えてください",
  ],
  "plugin-proposal": [
    "プラグインの概要を教えてください",
    "プラグインの機能について教えてください",
    "導入方法を教えてください",
  ],
  "app-design": [
    "アプリケーション設計の概要を教えてください",
    "アーキテクチャについて説明してください",
    "データモデルについて教えてください",
  ],
  "field-design": [
    "フィールド設計の概要を教えてください",
    "データ項目について説明してください",
    "バリデーションルールについて教えてください",
  ],
  "customize-specification": [
    "カスタマイズ仕様の概要を教えてください",
    "カスタマイズ範囲について教えてください",
    "実装方法を教えてください",
  ],
};

const ChatSuggestion: React.FC<ChatSuggestionProps> = ({ onSuggestionClick, documentType }) => {
  // ドキュメントタイプに応じたサジェスチョンを取得
  // 型安全性が保証されているため、undefinedになることはない
  const displaySuggestions = DEFAULT_SUGGESTIONS[documentType];

  const handleClick = (suggestion: string) => {
    onSuggestionClick(suggestion);
  };

  return (
    <div className={styles["chatSuggestionContainer"]} data-testid="chat-suggestion">
      <h3 className={styles["suggestionTitle"]}>こんなことが質問できます</h3>
      <div className={styles["suggestionList"]}>
        {displaySuggestions.map((suggestion, index) => (
          <button
            key={index}
            className={styles["suggestionButton"]}
            onClick={() => handleClick(suggestion)}
            data-testid={`suggestion-button-${index}`}
            type="button"
          >
            <span className={styles["suggestionText"]}>{suggestion}</span>
            <span className={styles["suggestionArrow"]}>→</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChatSuggestion;
